/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import _ from "underscore";
import React from "react";

import {
  defaultRequiredFields,
  externalIdentifierProviders,
  inactiveReasonsDisplay,
  redemptionLocationMethods,
  redemptionMethodProviders,
  twitterHandleDistributionMethods,
  websiteDistributionMethods,
  websiteRedemptionMethods,
} from "./constants";

export const getDenominationState = (inactiveReasons) => {
  if (inactiveReasons.includes("admin_disabled")) {
    return "disabled";
  }
  return "enabled";
};

export const getErrorMessage = (error) => {
  return error.response && error.response.data.errors
    ? error.response.data.errors.join(" ")
    : `${error.message}.`;
};

export const getFormattedData = (itemDetails, defaultRedemptionMethod) => {
  // NOTE: If the legacy id is undefined or an empty string it will be
  // falsy and updated to null.
  itemDetails.legacyId = itemDetails.legacyId ? itemDetails.legacyId : null;
  // NOTE: This formats the data in the way the back end is expected when updating
  //       or creating a new catalog item. Using a deep copy so itemDetails is not mutated
  const itemCopy = JSON.parse(JSON.stringify(itemDetails));
  const formattedData = {
    catalogItem: {
      announceable: itemCopy.announceable,
      autoclaimable: itemCopy.redemptionMethod.key === "click_claim",
      backfillable: itemCopy.backfillable,
      brand_name: trimText(itemCopy.brandName),
      category_ids: itemCopy.categories.map((category) => category.id),
      claim_url: itemCopy.claimUrl,
      claim_url_force_trailing_slash:
        itemCopy.claimUrlForceTrailingSlash || false,
      country_ids: itemCopy.countries.map((country) => country.id),
      currency_id: itemCopy.currency.id,
      description: trimText(itemCopy.description),
      distribution_method: itemCopy.distributionMethod,
      effective_at: itemCopy.effectiveAt || undefined,
      external_identifier: trimText(itemCopy.externalIdentifier),
      fee_percentage: parseFloat(itemCopy.feePercentage),
      flat_fee_in_usd_cents: parseInt(itemCopy.flatFeeInUsdCents),
      for_all_countries: itemCopy.forAllCountries,
      full_name: trimText(itemCopy.fullName),
      internally_fulfilled: itemCopy.redemptionMethod.key === "allowance_boost",
      legacy_id: itemCopy.legacyId,
      promoted: itemCopy.promoted,
      provider: itemCopy.provider,
      redemption_location: trimText(itemCopy.redemptionLocation),
      redemption_method_id:
        itemCopy.redemptionMethod.id || defaultRedemptionMethod.id,
      show_button_and_instructions:
        itemCopy.redemptionMethod.key === "claim_button_and_secret_code",
      twitter_handle: trimText(itemCopy.twitterHandle),
      warnings: itemCopy.warnings.filter((warning) => {
        return warning !== "";
      }),
      website: trimText(itemCopy.website),
    },
    denominations: itemCopy.denominations.map((denomination) => {
      return getFormattedDenomination(denomination, itemCopy.currency);
    }),
  };

  // NOTE: only send logo_attributes if they are new or changed
  // the imageBase64String is used to check because this gets created after uploading an image
  if (itemDetails.logo && itemDetails.logo.imageBase64String) {
    formattedData.catalogItem.logo_attributes = {
      alt_text: itemCopy.logo.altText,
      id: itemCopy.logo.id,
      image_content_type: itemCopy.logo.imageContentType,
      image_data: itemCopy.logo.imageBase64String,
      image_name: itemCopy.logo.imageName,
    };
  }

  return formattedData;
};

export const getFormattedDenomination = (denomination, currency) => {
  if (currency.exponent === undefined) {
    throw new Error(
      `Did not receive currency exponent data to update the ${denomination.amount} denomination. Currency ${currency.name} was missing
        exponent data`
    );
  }

  return {
    archived_at: denomination.archivedAt,
    external_identifier: denomination.externalIdentifier,
    id: denomination.id,
    inactive_reasons: denomination.inactiveReasons,
    legacy_id: denomination.legacyId,
    price_in_subunits: denomination.amount
      ? parseFloat(denomination.amount, 10) * 10 ** parseInt(currency.exponent)
      : denomination.priceInSubunits,
    state: denomination.state,
  };
};

export const getInactiveReasons = (nextState, oldInactiveReasons) => {
  // NOTE: toggling active/inactive only effects the "admin_disabled" reason
  if (nextState === "enabled") {
    return oldInactiveReasons.filter((reason) => reason !== "admin_disabled");
  } else {
    return [...oldInactiveReasons, "admin_disabled"];
  }
};

export const getInactiveReasonsDisplay = (reasons) => {
  if (!reasons || reasons.length === 0) return "N/A";

  const uniqueReasons = _.uniq(reasons);
  const displayReasons = uniqueReasons.map((reason) => {
    return inactiveReasonsDisplay[reason] || "Unknown";
  });

  return displayReasons.map((reason, index) => {
    return <div key={`reason-${index}`}>{reason}</div>;
  });
};

export const getRequiredFields = (itemDetails) => {
  const { distributionMethod, provider, redemptionMethod } = itemDetails;
  const requiredFields = [...defaultRequiredFields];

  if (externalIdentifierProviders.includes(provider)) {
    requiredFields.push("externalIdentifier");
  }

  if (redemptionMethodProviders.includes(provider)) {
    requiredFields.push("redemptionMethod");
  }

  if (
    claimUrlIsRequired(
      itemDetails.redemptionMethod,
      itemDetails.distributionMethod,
      itemDetails.provider
    )
  ) {
    requiredFields.push("claimUrl");
  }

  if (redemptionLocationMethods.includes(redemptionMethod.key)) {
    requiredFields.push("redemptionLocation");
  }

  if (
    websiteRedemptionMethods.includes(redemptionMethod.key) ||
    websiteDistributionMethods.includes(distributionMethod)
  ) {
    requiredFields.push("website");
  }

  if (twitterHandleDistributionMethods.includes(distributionMethod)) {
    requiredFields.push("twitterHandle");
  }

  return requiredFields;
};

export const claimUrlIsRequired = (
  redemptionMethod,
  distributionMethod,
  provider
) => {
  const requiresClaimButton = [
    "click_claim",
    "claim_button_and_secret_code",
  ].includes(redemptionMethod.key);
  const isGiftCard = distributionMethod === "gift_card";
  const isAmazon = provider === "amazon";

  return (requiresClaimButton && isGiftCard) || isAmazon;
};

export const hasValue = (fieldValue) => {
  if (Array.isArray(fieldValue)) {
    return fieldValue.length > 0;
  }
  if (typeof fieldValue === "object" && fieldValue !== null) {
    return Object.keys(fieldValue).length > 0;
  }
  return !!fieldValue;
};

const trimText = (text) => {
  return text ? text.trim() : "";
};
